/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import {useMemo} from "react";

export const useStandardizedOptions = (questionOptions, primaryKey = 'value', labelKey = 'label') => {
	
	// some questions have {} or [] as the Question.options
	// which can be turned into an Array instead of an Object
	// and will then have a prototype member function called
	// values which is not what we want.
	let optionItems = questionOptions?.hasOwnProperty('values') ? (questionOptions.values ?? []) : [];
	
	if(!optionItems || optionItems?.length < 1) {
		optionItems = [
			{[primaryKey]: 1, [labelKey]: "Yes"},
			{[primaryKey]: 0, [labelKey]: "No"}
		]
	}
	
	// some questions defined {code:, name:} pairs
	// this attempts to standardize that behavior
	for (const optionItem of optionItems) {
		if (!optionItem.hasOwnProperty(primaryKey) && optionItem.hasOwnProperty('code')) {
			optionItem[primaryKey] = optionItem['code'];
			delete optionItem['code'];
		}
		
		if (!optionItem.hasOwnProperty(labelKey) && optionItem.hasOwnProperty('name')) {
			optionItem[labelKey] = optionItem['name'];
			delete optionItem['name'];
		}
	}
	
	return optionItems;
}

export const useOnlyWhen = () => {
	
	/**
	 * PARALLEL LOGIC — keep in sync with:
	 *   server/app/lib/App/Service/SurveySubmissionService.php :: isQuestionVisible()
	 * If this function changes, update the PHP counterpart too (and vice versa).
	 */
	const isFieldVisible = (options, answers) => {
		const { onlyWhen = null } = options ?? {};
		
		if (onlyWhen) {
			// get the keys which affect the rerender
			const relevantAnswerKeys = Object.keys(onlyWhen)
			return relevantAnswerKeys.every(key => String(answers[key] ?? '') === String(onlyWhen[key] ?? ''));
		}
		
		return true;
	}
	
	const getVisibleQuestions = (questions, friendlyAnswers = {}) => {
		if (!questions || !friendlyAnswers) {
			return [];
		}
		return questions
			.filter(question => isFieldVisible(question.options, friendlyAnswers))
			.sort((a, b) => Number(a.order) - Number(b.order));
	}
	
	return {
		isFieldVisible,
		getVisibleQuestions
	};
}

export const allSurveyQuestions = (survey) => (
	survey?.SurveySections?.reduce((carry, section) => carry.concat(section.SurveyQuestions), []) ?? []
)

export const useSurveyQuestion = (survey, questionId) => {
	const questions = allSurveyQuestions(survey);
	
	const question = questions.find((question) => String(question.surveyQuestionId) === String(questionId));
	
	return question ?? null;
}

export const useSurveyQuestions = (survey, indexBy = 'surveyQuestionId') => {
	return useMemo(() => {
		const questions = allSurveyQuestions(survey);
		
		return questions.reduce((carry, question) => {
			carry[question[indexBy]] = question;
			return carry;
		}, {});
	},
		[survey, indexBy]
	);
}

/**
 * PARALLEL LOGIC — keep in sync with:
 *   server/app/lib/App/Service/SurveySubmissionService.php :: getFriendlyAnswersForSection()
 * If this function changes, update the PHP counterpart too (and vice versa).
 */
export const getFriendlyAnswersForQuestions = (answers, questions) => {
	if (!answers || !questions) {
		return {};
	}
	// SB-3022: answers resolve to questions by `code`. Iterating the survey's questions yields exactly
	// one entry per question; each is matched to its answer by code, so an answer carried over from
	// another subprogram's survey (stale surveyQuestionId, same code) still surfaces. Every answer
	// carries its question's code (denormalized SurveyAnswer.code; the survey form sets it on new
	// answers too), so there is no surveyQuestionId fallback.
	return Object.fromEntries(questions.map((question) => {
		if (!question?.code) return null;
		const answer = answers.find((a) => (a.code != null && a.code !== '') && String(a.code) === String(question.code));
		return answer ? [
			question.code, // key
			(answer.valueBool ?? answer.valueNumeric ?? answer.valueText ?? null) //value
		] : null
	}).filter(Boolean));
}

/*
 * This function takes the answers and survey and returns an object
 * with the question code as the key and the answer value as the value.
 * It uses useMemo to memoize the result for performance.
 */
export const useFriendlyAnswers = (answers, survey) => {
	
	const allQuestions = allSurveyQuestions(survey);
	
	return useMemo(
		() => getFriendlyAnswersForQuestions(answers, allQuestions),
		[JSON.stringify(answers), JSON.stringify(allQuestions)]
	);
}
